import {
  COMMERCE_CURRENCY,
  COMMERCE_PRODUCTS,
  CommerceProduct,
  DRINKS_COLLECTION_ID,
  getCommerceProduct,
} from "constants/commerce";

const POPPI_BRAND = "Poppi";
const DRINKS_COLLECTION_NAME = "Drinks";
const UUID_BYTE_LENGTH = 16;

let fallbackEventSequence = 0;

export interface CommerceAnalyticsItem {
  item_id: string;
  item_name: string;
  item_brand: typeof POPPI_BRAND;
  item_category: CommerceProduct["type"];
  item_variant: CommerceProduct["variant"];
  price: number;
  quantity: 1;
  product_id: string;
  variant_id: string;
  index?: number;
}

export interface CommerceBusinessProduct {
  id: string;
  name: string;
  variant_id: string;
  sku?: string;
  price: number;
  currency: typeof COMMERCE_CURRENCY;
  product_type: CommerceProduct["type"];
  variant: CommerceProduct["variant"];
}

export interface CollectionViewedEvent {
  event: "collection_viewed";
  event_id: string;
  collection: {
    id: string;
    name: typeof DRINKS_COLLECTION_NAME;
    handle: "drinks";
    products: CommerceBusinessProduct[];
  };
  currency: typeof COMMERCE_CURRENCY;
  value: number;
}

export interface ProductViewedEvent {
  event: "product_viewed";
  event_id: string;
  product: CommerceBusinessProduct;
  currency: typeof COMMERCE_CURRENCY;
  value: number;
}

export interface ViewItemListEvent {
  event: "view_item_list";
  event_id: string;
  ecommerce: {
    currency: typeof COMMERCE_CURRENCY;
    value: number;
    item_list_id: string;
    item_list_name: typeof DRINKS_COLLECTION_NAME;
    items: CommerceAnalyticsItem[];
  };
}

export interface ViewItemEvent {
  event: "view_item";
  event_id: string;
  ecommerce: {
    currency: typeof COMMERCE_CURRENCY;
    value: number;
    items: CommerceAnalyticsItem[];
  };
  content_ids: string[];
  content_name: string;
  content_type: "product";
  contents: Array<{
    id: string;
    quantity: 1;
    item_price: number;
  }>;
  currency: typeof COMMERCE_CURRENCY;
  value: number;
}

export interface EcommerceClearEvent {
  ecommerce: null;
}

export type CommerceDataLayerEntry =
  | CollectionViewedEvent
  | ProductViewedEvent
  | ViewItemListEvent
  | ViewItemEvent
  | EcommerceClearEvent;

interface CommerceDataLayerWindow {
  dataLayer?: CommerceDataLayerEntry[];
}

function createUuid(crypto: Crypto): string {
  const bytes = crypto.getRandomValues(new Uint8Array(UUID_BYTE_LENGTH));

  // RFC 4122 version 4 and variant bits.
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, "0")
  ).join("");

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(
    12,
    16
  )}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function createFallbackEventId(): string {
  fallbackEventSequence += 1;

  const highResolutionTime =
    typeof performance !== "undefined" && typeof performance.now === "function"
      ? Math.floor(performance.now() * 1000).toString(36)
      : "0";

  return `commerce-${Date.now().toString(
    36
  )}-${highResolutionTime}-${fallbackEventSequence.toString(36)}`;
}

function createEventId(): string {
  const browserCrypto =
    typeof window === "undefined" ? undefined : window.crypto;

  if (typeof browserCrypto?.randomUUID === "function") {
    return browserCrypto.randomUUID();
  }

  if (typeof browserCrypto?.getRandomValues === "function") {
    return createUuid(browserCrypto);
  }

  // Keep analytics non-blocking in legacy or non-browser runtimes that do not
  // expose Web Crypto. The sequence prevents same-runtime collisions.
  return createFallbackEventId();
}

function roundCurrency(value: number): number {
  return Number(value.toFixed(2));
}

function toAnalyticsItem(
  product: CommerceProduct,
  index?: number
): CommerceAnalyticsItem {
  return {
    item_id: product.variantId,
    item_name: product.name,
    item_brand: POPPI_BRAND,
    item_category: product.type,
    item_variant: product.variant,
    price: product.price,
    quantity: 1,
    product_id: product.productId,
    variant_id: product.variantId,
    ...(index === undefined ? {} : { index }),
  };
}

function toBusinessProduct(product: CommerceProduct): CommerceBusinessProduct {
  return {
    id: product.productId,
    name: product.name,
    variant_id: product.variantId,
    ...(product.sku ? { sku: product.sku } : {}),
    price: product.price,
    currency: COMMERCE_CURRENCY,
    product_type: product.type,
    variant: product.variant,
  };
}

/**
 * Builds the semantic collection event and its GA4 counterpart. The ecommerce
 * reset deliberately sits immediately before view_item_list so GTM cannot
 * reuse items from a previous ecommerce event.
 */
export function buildCollectionViewEvents(
  products: readonly CommerceProduct[] = COMMERCE_PRODUCTS,
  eventId: string = createEventId()
): [CollectionViewedEvent, EcommerceClearEvent, ViewItemListEvent] {
  const value = roundCurrency(
    products.reduce((total, product) => total + product.price, 0)
  );
  const businessProducts = products.map(toBusinessProduct);
  const items = products.map((product, index) =>
    toAnalyticsItem(product, index + 1)
  );

  return [
    {
      event: "collection_viewed",
      event_id: eventId,
      collection: {
        id: DRINKS_COLLECTION_ID,
        name: DRINKS_COLLECTION_NAME,
        handle: "drinks",
        products: businessProducts,
      },
      currency: COMMERCE_CURRENCY,
      value,
    },
    { ecommerce: null },
    {
      event: "view_item_list",
      event_id: eventId,
      ecommerce: {
        currency: COMMERCE_CURRENCY,
        value,
        item_list_id: DRINKS_COLLECTION_ID,
        item_list_name: DRINKS_COLLECTION_NAME,
        items,
      },
    },
  ];
}

/**
 * Builds the semantic product event plus GA4/Meta-ready view_item data. GTM is
 * the only fan-out point: this module intentionally never calls gtag or fbq.
 */
export function buildProductViewEvents(
  product: CommerceProduct,
  eventId: string = createEventId()
): [ProductViewedEvent, EcommerceClearEvent, ViewItemEvent] {
  const item = toAnalyticsItem(product);

  return [
    {
      event: "product_viewed",
      event_id: eventId,
      product: toBusinessProduct(product),
      currency: COMMERCE_CURRENCY,
      value: product.price,
    },
    { ecommerce: null },
    {
      event: "view_item",
      event_id: eventId,
      ecommerce: {
        currency: COMMERCE_CURRENCY,
        value: product.price,
        items: [item],
      },
      content_ids: [product.productId],
      content_name: product.name,
      content_type: "product",
      contents: [
        {
          id: product.productId,
          quantity: 1,
          item_price: product.price,
        },
      ],
      currency: COMMERCE_CURRENCY,
      value: product.price,
    },
  ];
}

export function pushCommerceDataLayerEntries(
  entries: readonly CommerceDataLayerEntry[]
): boolean {
  if (typeof window === "undefined") return false;

  const analyticsWindow = window as unknown as CommerceDataLayerWindow;
  analyticsWindow.dataLayer ??= [];
  entries.forEach((entry) => analyticsWindow.dataLayer?.push(entry));
  return true;
}

export function logCommerceCollectionView(): boolean {
  return pushCommerceDataLayerEntries(buildCollectionViewEvents());
}

export function logCommerceProductView(handle: string): boolean {
  const product = getCommerceProduct(handle);
  if (!product) return false;

  return pushCommerceDataLayerEntries(buildProductViewEvents(product));
}
